import React, {CSSProperties, useRef} from 'react'

import {IconButton, Popover, ListItem, S1, Info} from '../index.js'
import {InternalColumn} from './types.js'
import {getCellStyle, getHeaderTitleStyle, getMenuStyle, resizerStyle, tableColors, tableMenus, tableSpacing} from './styles.js'
import {isSortable} from './utils.js'
import t from '../../../helpers/Languages.js'
import type {PopoverRef} from '../overlays/Popover.js'

interface TableHeaderCellProps {
    column: InternalColumn
    width: number
    sorted: boolean
    sortDecending: boolean
    onSort: (id: string) => void
    option?: string
    onSelectOption: (column: InternalColumn, value: string) => void
    resizable: boolean
    resizing: boolean
    onResizeStart: (id: string, event: React.MouseEvent) => void
    infoLeft: boolean
    menuMaxHeight: number
}

const TableHeaderCell = ({column, width, sorted, sortDecending, onSort, option, onSelectOption, resizable, resizing, onResizeStart, infoLeft, menuMaxHeight}: TableHeaderCellProps) => {
    const popoverRef = useRef<PopoverRef>(null)

    // A column without anything to sort on (only render) has no sort affordance
    const sortable = !!column.title && isSortable(column)

    const wrapperStyle: CSSProperties = {
        ...getCellStyle(width),
        position: 'relative',
        userSelect: resizing ? 'none' : undefined
    }

    // Sortable titles are buttons: Enter and Space sort like a click
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSort(column.id)
        }
    }

    const ariaSort = sortable ? sorted ? sortDecending ? 'descending' : 'ascending' : 'none' : undefined

    return (
        <div role='columnheader' aria-sort={ariaSort} style={wrapperStyle}>
            <S1
                style={getHeaderTitleStyle({sortable})}
                role={sortable ? 'button' : undefined}
                tabIndex={sortable ? 0 : undefined}
                onClick={sortable ? () => onSort(column.id) : undefined}
                onKeyDown={sortable ? onKeyDown : undefined}
            >
                {column.title || ''}

                {sorted && (
                    sortDecending ?
                        <i style={{marginLeft: tableSpacing.sortIconGap}} className='mdi mdi-sort-descending' aria-hidden /> :
                        <i style={{marginLeft: tableSpacing.sortIconGap}} className='mdi mdi-sort-ascending' aria-hidden />
                )}
            </S1>

            {column.info && (
                <Info
                    style={{marginLeft: 6}}
                    placement={infoLeft ? 'left' : undefined}
                    color={tableColors.headerText}
                    text={column.info}
                />
            )}

            {column.dropdown && sorted &&
                <Popover
                    ref={popoverRef}
                    content={
                        <div style={getMenuStyle(tableMenus.dropdownWidth, menuMaxHeight)}>
                            {column.dropdown.options.map((dropdownOption, optionIndex) => (
                                <ListItem
                                    key={optionIndex}
                                    onClick={() => {
                                        popoverRef.current?.close()
                                        onSelectOption(column, dropdownOption.value)
                                    }}
                                    focused={option === dropdownOption.value}
                                >
                                    {dropdownOption.label}
                                </ListItem>
                            ))}
                        </div>
                    }
                >
                    <IconButton aria-label={t('tableSortOptions')} aria-haspopup='menu'>
                        <i className='mdi mdi-chevron-down' />
                    </IconButton>
                </Popover>
            }

            {resizable &&
                <div
                    style={resizerStyle}
                    onMouseDown={(event) => onResizeStart(column.id, event)}
                />
            }
        </div>
    )
}

export default TableHeaderCell
